// src/components/ChatGPTCodeGenerator.jsx
import React, { useState } from 'react';
import { callChatGPT, extractCodeFromResponse, CHATGPT_CONFIG } from '../config/chatgpt';

// 共通パーツ用のコード抽出関数
const extractCommonPartCode = (response) => {
    const htmlMatch = response.match(/```html\s*([\s\S]*?)\s*```/);
    const cssMatch = response.match(/```css\s*([\s\S]*?)\s*```/);

    return {
        html: htmlMatch ? htmlMatch[1].trim() : '',
        css: cssMatch ? cssMatch[1].trim() : '',
        fullHTML: htmlMatch ? htmlMatch[1].trim() : '',
        explanation: response
    };
};
import '../styles/components/ChatGPTCodeGenerator.scss';

const ChatGPTCodeGenerator = ({ onCodeGenerated, isOpen, onClose, defaultPalette = 'default', availablePalettes = [], pageSettings = {}, partType = null, partName = '' }) => {
    const [userRequest, setUserRequest] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedCode, setGeneratedCode] = useState(null);
    const [error, setError] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    // コード生成を実行
    const handleGenerateCode = async () => {
        if (!userRequest.trim()) {
            setError('要望を入力してください。');
            return;
        }

        setIsGenerating(true);
        setError(null);
        setGeneratedCode(null);

        try {
            // デフォルトパレットの色情報を取得
            const defaultPaletteInfo = availablePalettes.find(p => p.key === defaultPalette) ||
                availablePalettes.find(p => p.key === 'default') ||
                { name: 'デフォルト', colors: {} };

            // カラーパレット情報をプロンプトに追加
            let colorInfo = '';
            if (pageSettings?.disableColorPalette) {
                // カラーパレット無効の場合
                colorInfo = `\n\n【カラーパレット情報】\nこのページではカラーパレットを使用しません。色は直接指定してください（例：#FF0000, rgb(255,0,0), blue など）。`;
            } else if (pageSettings?.colorPalette) {
                // 特定パレット指定の場合：全パレットから選択可能
                const availablePalettesList = availablePalettes.map(p => `- ${p.name}（${p.key}）`).join('\n');
                colorInfo = `\n\n【カラーパレット情報】\n以下のカラーパレットから適切なものを選択して使用してください：\n${availablePalettesList}\n\n現在のページ設定：「${availablePalettes.find(p => p.key === pageSettings.colorPalette)?.name || pageSettings.colorPalette}」\n\n重要：選択したパレットの色は必ずCSS変数として使用してください（例：var(--color-primary), var(--color-background)など）。\n\n基本的なCSS変数：\n- var(--color-primary) - メインカラー\n- var(--color-secondary) - セカンダリカラー\n- var(--color-accent) - アクセントカラー\n- var(--color-background) - 背景色\n- var(--color-surface) - サーフェス色\n- var(--color-text) - テキスト色\n- var(--color-textMuted) - ミュートテキスト色\n- var(--color-border) - ボーダー色`;
            } else {
                // デフォルトパレット使用の場合：自動適用
                colorInfo = `\n\n【カラーパレット情報】\nデフォルトパレット「${defaultPaletteInfo.name}」の色を使用してください：\n${Object.entries(defaultPaletteInfo.colors).map(([key, value]) => `- ${key}: ${value}`).join('\n')}\n\n重要：これらの色は必ずCSS変数として使用してください（例：var(--color-primary), var(--color-background)など）。\n直接色コードを書かず、必ずCSS変数を使用することで、ユーザーがカラーパレットを変更した際に自動的に色が更新されます。\n\n利用可能なCSS変数：\n${Object.keys(defaultPaletteInfo.colors).map(key => `- var(--color-${key})`).join('\n')}`;
            }

            // プロンプトテンプレートを選択
            let prompt;
            if (partType) {
                // 共通パーツ用のプロンプト
                const partTypeLabel = {
                    'header': 'ヘッダー',
                    'footer': 'フッター',
                    'navigation': 'ナビゲーション',
                    'sidebar': 'サイドバー',
                    'cta': 'CTA（行動喚起）'
                }[partType] || partType;

                prompt = CHATGPT_CONFIG.PROMPTS.COMMON_PART_DESIGN
                    .replace('{partType}', partTypeLabel)
                    .replace('{userRequest}', userRequest + colorInfo);
            } else {
                // 通常のWebサイト用のプロンプト
                prompt = CHATGPT_CONFIG.PROMPTS.WEBSITE_DESIGN.replace('{userRequest}', userRequest + colorInfo);
            }

            // ChatGPT APIを呼び出し
            const response = await callChatGPT(prompt);

            // レスポンスからコードを抽出
            let extractedCode;
            if (partType) {
                // 共通パーツ用の抽出
                extractedCode = extractCommonPartCode(response);
            } else {
                // 通常のWebサイト用の抽出
                extractedCode = extractCodeFromResponse(response);
            }

            setGeneratedCode(extractedCode);

        } catch (err) {
            console.error('コード生成エラー:', err);
            setError(err.message || 'コード生成中にエラーが発生しました。');
        } finally {
            setIsGenerating(false);
        }
    };

    // 生成されたコードを適用
    const handleApplyCode = () => {
        if (generatedCode && onCodeGenerated) {
            onCodeGenerated(generatedCode);
            onClose();
        }
    };

    // コード生成をリセット
    const handleReset = () => {
        setUserRequest('');
        setGeneratedCode(null);
        setError(null);
        setShowPreview(false);
    };

    if (!isOpen) return null;

    return (
        <div className="chatgpt-code-generator-overlay">
            <div className="chatgpt-code-generator-modal">
                <div className="modal-header">
                    <h2>🤖 ChatGPT コード生成</h2>
                    <button className="close-button" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="modal-content">
                    {/* ユーザー入力セクション */}
                    <div className="input-section">
                        <label htmlFor="userRequest">
                            どのようなWebサイトにしたいか教えてください：
                        </label>
                        <textarea
                            id="userRequest"
                            value={userRequest}
                            onChange={(e) => setUserRequest(e.target.value)}
                            placeholder="例：モダンな企業サイトで、青を基調としたデザインで、会社概要、サービス紹介、お問い合わせフォームを含めたい..."
                            rows={4}
                            disabled={isGenerating}
                        />

                        <button
                            className="generate-button"
                            onClick={handleGenerateCode}
                            disabled={isGenerating || !userRequest.trim()}
                        >
                            {isGenerating ? '生成中...' : '🚀 コード生成'}
                        </button>
                    </div>

                    {/* エラーメッセージ */}
                    {error && (
                        <div className="error-message">
                            ❌ {error}
                        </div>
                    )}

                    {/* 生成されたコード表示 */}
                    {generatedCode && (
                        <div className="generated-code-section">
                            <div className="code-header">
                                <h3>✨ 生成されたコード</h3>
                                <div className="code-actions">
                                    <button
                                        className="preview-button"
                                        onClick={() => setShowPreview(!showPreview)}
                                    >
                                        {showPreview ? 'コード表示' : 'プレビュー'}
                                    </button>
                                    <button
                                        className="apply-button"
                                        onClick={handleApplyCode}
                                    >
                                        ✅ 適用
                                    </button>
                                </div>
                            </div>

                            {showPreview ? (
                                <div className="code-preview">
                                    <div className="preview-header">
                                        <h4>プレビュー</h4>
                                    </div>
                                    <div
                                        className="preview-content"
                                        dangerouslySetInnerHTML={{
                                            __html: `
                                                <style>${generatedCode.headCSS}</style>
                                                ${generatedCode.bodyHTML}
                                            `
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className="code-tabs">
                                    {/* HTML (完全なHTMLファイル) タブ */}
                                    <div className="code-tab">
                                        <h4>HTML (完全なHTMLファイル)</h4>
                                        <pre className="code-block">
                                            <code>{generatedCode.fullHTML}</code>
                                        </pre>
                                    </div>

                                    {/* 説明タブ */}
                                    <div className="code-tab">
                                        <h4>説明</h4>
                                        <div className="explanation">
                                            {generatedCode.explanation}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 説明 */}
                            {!showPreview && (
                                <div className="explanation-section">
                                    <h4>💡 生成されたコードについて</h4>
                                    <p>{generatedCode.explanation}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ヒント */}
                    <div className="hints">
                        <h4>💡 より良い結果を得るためのヒント</h4>
                        <ul>
                            <li>具体的なデザインの好みを伝える（色、スタイル、レイアウトなど）</li>
                            <li>含めたい機能やセクションを明記する</li>
                            <li>ターゲットユーザーや目的を説明する</li>
                            <li>参考にしたいサイトがあればURLを共有する</li>
                        </ul>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="reset-button" onClick={handleReset}>
                        🔄 リセット
                    </button>
                    <button className="close-button-secondary" onClick={onClose}>
                        閉じる
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatGPTCodeGenerator; 